import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Assessment,
  CheckCircleOutline,
  DirectionsCar,
  QueryStats,
  SwapHoriz,
} from '@mui/icons-material';
import { caseService } from '../services/api';
import { countBy, filterCasesByDate, toDateInput } from '../utils/reporting';
import { usePortalText } from '../locales/portalTranslations';

const STATUS_COLORS = {
  Open: '#2563eb',
  Pending: '#60a5fa',
  Processed: '#0891b2',
  Reassigned: '#f59e0b',
  Recommended: '#16a34a',
  Approved: '#22c55e',
  'Not Recommended': '#dc2626',
  Rejected: '#ef4444',
  Expired: '#7c3aed',
};

const GraphsPage = () => {
  const theme = useTheme();
  const pt = usePortalText();
  const [cases, setCases] = useState([]);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    caseService.getAllCases()
      .then((response) => setCases(response.data.cases || []))
      .catch(() => setError(pt('analyticsError')))
      .finally(() => setLoading(false));
  }, []);

  const filteredCases = useMemo(() => {
    if (period === 'all') return cases;
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - Number(period) + 1);
    return filterCasesByDate(cases, toDateInput(start), toDateInput(end));
  }, [cases, period]);

  const statusData = useMemo(() => Object.entries(countBy(filteredCases, 'status'))
    .sort((a, b) => b[1] - a[1]), [filteredCases]);
  const makeData = useMemo(() => Object.entries(countBy(filteredCases, 'make'))
    .sort((a, b) => b[1] - a[1]).slice(0, 6), [filteredCases]);

  const decisionCount = filteredCases.filter((item) => ['Recommended', 'Approved', 'Not Recommended', 'Rejected'].includes(item.status)).length;
  const recommendedCount = filteredCases.filter((item) => ['Recommended', 'Approved'].includes(item.status)).length;
  const reassignedCount = filteredCases.filter((item) => item.status === 'Reassigned').length;
  const completedCount = filteredCases.filter((item) => !['Open', 'Pending'].includes(item.status)).length;
  const total = filteredCases.length;
  const completionRate = total ? Math.round((completedCount / total) * 100) : 0;
  const recommendationRate = decisionCount ? Math.round((recommendedCount / decisionCount) * 100) : 0;

  const dailyData = useMemo(() => {
    const days = period === 'all' ? 14 : Math.min(Number(period), 14);
    const safeDays = Number.isFinite(days) ? Math.max(days, 1) : 14;
    return Array.from({ length: safeDays }, (_, index) => {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() - safeDays + index + 1);
      const key = toDateInput(date);
      return {
        label: date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
        count: filteredCases.filter((item) => toDateInput(new Date(item.created_at || item.updated_at)) === key).length,
      };
    });
  }, [filteredCases, period]);

  const maxDaily = Math.max(1, ...dailyData.map((item) => item.count));
  const chartWidth = 720;
  const chartHeight = 220;
  const points = dailyData.map((item, index) => {
    const x = dailyData.length === 1 ? chartWidth / 2 : 24 + (index * (chartWidth - 48)) / (dailyData.length - 1);
    const y = chartHeight - 30 - (item.count / maxDaily) * 160;
    return `${x},${y}`;
  }).join(' ');

  if (loading) return <Box sx={{ minHeight: 420, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const kpis = [
    { label: pt('casesPeriod'), value: total, helper: pt('requestsCreated'), icon: <Assessment />, color: theme.palette.primary.main },
    { label: pt('completionRate'), value: `${completionRate}%`, helper: `${completedCount}`, icon: <CheckCircleOutline />, color: theme.palette.success.main },
    { label: pt('recommendationRate'), value: `${recommendationRate}%`, helper: `${recommendedCount}`, icon: <QueryStats />, color: '#0891b2' },
    { label: pt('reassigned'), value: reassignedCount, helper: total ? `${Math.round((reassignedCount / total) * 100)}%` : '0%', icon: <SwapHoriz />, color: theme.palette.warning.main },
  ];

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={750}>{pt('analytics')}</Typography>
          <Typography color="text.secondary">{pt('analyticsHelp')}</Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={period}
          onChange={(_, value) => value && setPeriod(value)}
          aria-label="Analytics period"
        >
          <ToggleButton value="1">{pt('today')}</ToggleButton>
          <ToggleButton value="7">{pt('days7')}</ToggleButton>
          <ToggleButton value="30">{pt('days30')}</ToggleButton>
          <ToggleButton value="all">{pt('allTime')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        {kpis.map((item) => (
          <Paper key={item.label} variant="outlined" sx={{ p: 2.25, borderRadius: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                <Typography variant="h4" fontWeight={750} sx={{ my: 0.5 }}>{item.value}</Typography>
                <Typography variant="caption" color="text.secondary">{item.helper}</Typography>
              </Box>
              <Box sx={{ width: 44, height: 44, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: alpha(item.color, 0.1), color: item.color }}>
                {item.icon}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.55fr) minmax(320px, 0.85fr)' }, gap: 2, mb: 2 }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2.5, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700}>{pt('volumeTrend')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{pt('casesByDay')}</Typography>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Box component="svg" viewBox={`0 0 ${chartWidth} ${chartHeight}`} role="img" aria-label="Daily case volume line graph" sx={{ width: '100%', minWidth: 520, display: 'block' }}>
              {[30, 70, 110, 150, 190].map((y) => <line key={y} x1="24" x2={chartWidth - 24} y1={y} y2={y} stroke={theme.palette.divider} strokeWidth="1" />)}
              <polyline points={points} fill="none" stroke={theme.palette.primary.main} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              {dailyData.map((item, index) => {
                const [x, y] = points.split(' ')[index].split(',');
                return <circle key={`${item.label}-${index}`} cx={x} cy={y} r="5" fill={theme.palette.background.paper} stroke={theme.palette.primary.main} strokeWidth="3"><title>{`${item.label}: ${item.count} cases`}</title></circle>;
              })}
              {dailyData.map((item, index) => {
                if (dailyData.length > 8 && index % Math.ceil(dailyData.length / 7) !== 0 && index !== dailyData.length - 1) return null;
                const x = dailyData.length === 1 ? chartWidth / 2 : 24 + (index * (chartWidth - 48)) / (dailyData.length - 1);
                return <text key={`label-${item.label}-${index}`} x={x} y="216" textAnchor="middle" fontSize="12" fill={theme.palette.text.secondary}>{item.label}</text>;
              })}
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
          <Typography variant="h6" fontWeight={700}>{pt('statusDistribution')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>{pt('currentStage')}</Typography>
          <Stack spacing={1.75}>
            {statusData.length ? statusData.map(([status, count]) => (
              <Box key={status}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                  <Typography variant="body2" fontWeight={600}>{status}</Typography>
                  <Typography variant="body2" color="text.secondary">{count}</Typography>
                </Box>
                <Box sx={{ height: 9, borderRadius: 8, bgcolor: 'grey.100', overflow: 'hidden' }}>
                  <Box sx={{ width: `${(count / Math.max(...statusData.map(([, value]) => value))) * 100}%`, height: '100%', borderRadius: 8, bgcolor: STATUS_COLORS[status] || 'grey.500' }} />
                </Box>
              </Box>
            )) : <Typography color="text.secondary">{pt('noCasesPeriod')}</Typography>}
          </Stack>
        </Paper>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
          <Typography variant="h6" fontWeight={700}>{pt('decisionMix')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>{pt('decisionHelp')}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <Box sx={{ width: 170, height: 170, borderRadius: '50%', background: `conic-gradient(${theme.palette.success.main} 0 ${recommendationRate}%, ${theme.palette.error.main} ${recommendationRate}% 100%)`, position: 'relative' }}>
              <Box sx={{ position: 'absolute', inset: 22, borderRadius: '50%', bgcolor: 'background.paper', display: 'grid', placeItems: 'center', textAlign: 'center' }}>
                <Box><Typography variant="h5" fontWeight={750}>{decisionCount}</Typography><Typography variant="caption" color="text.secondary">{pt('decisions')}</Typography></Box>
              </Box>
            </Box>
            <Stack spacing={1.5}>
              <Box><Typography variant="body2" color="text.secondary">{pt('recommended')}</Typography><Typography variant="h6" fontWeight={700} color="success.main">{recommendedCount}</Typography></Box>
              <Box><Typography variant="body2" color="text.secondary">{pt('notRecommended')}</Typography><Typography variant="h6" fontWeight={700} color="error.main">{Math.max(0, decisionCount - recommendedCount)}</Typography></Box>
            </Stack>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}><DirectionsCar color="primary" /><Typography variant="h6" fontWeight={700}>{pt('topMakes')}</Typography></Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>{pt('topMakesHelp')}</Typography>
          <Stack spacing={1.5}>
            {makeData.length ? makeData.map(([make, count], index) => (
              <Box key={make} sx={{ display: 'grid', gridTemplateColumns: '28px minmax(90px, 0.7fr) 2fr 32px', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">{index + 1}</Typography>
                <Typography variant="body2" fontWeight={600} noWrap>{make}</Typography>
                <Box sx={{ height: 10, bgcolor: 'grey.100', borderRadius: 8, overflow: 'hidden' }}><Box sx={{ width: `${(count / makeData[0][1]) * 100}%`, height: '100%', bgcolor: index === 0 ? 'primary.main' : 'primary.light', borderRadius: 8 }} /></Box>
                <Typography variant="body2" textAlign="right">{count}</Typography>
              </Box>
            )) : <Typography color="text.secondary">{pt('noVehicleData')}</Typography>}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default GraphsPage;
